import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AsyncPipe, SlicePipe } from '@angular/common';

import { MatDialog } from '@angular/material/dialog';
import {
  MatBottomSheet,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet';

import { filter, Subject, takeUntil } from 'rxjs';

import { SwiperOptions } from 'swiper/types/swiper-options';

import { SwiperDirective } from 'src/app/shared/directives/swiper.directive';

import { ESvgIcons } from 'src/app/core/enums/svg-icons';
import { ETripCityInfoType } from 'src/app/core/enums/trip-planner-view.enum';
import { OccasionGeniusEvent } from 'src/app/core/types/models/occasion-genius-event.model';
import { OccasionGeniusResponse } from 'src/app/core/types/models/occasion-genius-response.model';
import { IOccasionGeniusRecurringEvent } from 'src/app/core/types/interfaces/occasion-genius-event.interface';

import { AuthService } from 'src/app/core/services/auth.service';
import { PackageService } from 'src/app/core/services/package.service';
import { BreakpointService } from 'src/app/core/services/breakpoint.service';
import { HeaderStateService } from 'src/app/core/services/header-state.service';
import {
  GoogleAnalyticService,
  HeaderDesktopEvent,
} from 'src/app/core/services/google-analytic.service';

import { ShowAllMobileComponent } from '../show-all-mobile/show-all-mobile.component';
import { AuthDialogComponent } from 'src/app/shared/dialogs/auth-dialog/auth-dialog.component';
import { NoItemsFoundComponent } from 'src/app/shared/components/no-items-found/no-items-found.component';
import { OccasionGeniusCardMobileComponent } from '../occasion-genius-card-mobile/occasion-genius-card-mobile.component';

const OCCASION_LIST_SWIPER_OPTIONS: SwiperOptions = {
  slidesPerView: 1.2,
  spaceBetween: 16,
  autoplay: {
    delay: 7000,
  },
  breakpoints: {
    375: {
      slidesPerView: 1.2,
      spaceBetween: 16,
    },
    524: {
      slidesPerView: 1.2,
      spaceBetween: 20,
    },
    768: {
      slidesPerView: 1.5,
      spaceBetween: 24,
    },
    1100: {
      slidesPerView: 2,
      spaceBetween: 32,
    },
  },
};

@Component({
  selector: 'app-occasion-genius-list-mobile',
  templateUrl: './occasion-genius-list-mobile.component.html',
  styleUrls: ['occasion-genius-list-mobile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    SlicePipe,
    SwiperDirective,
    NoItemsFoundComponent,
    OccasionGeniusCardMobileComponent,
  ],
})
export class OccasionGeniusListMobileComponent implements OnInit, OnDestroy {
  @Input() title: 'Events and Occasions' | 'Event Calendar' =
    'Events and Occasions';
  @Input() isFetching: boolean = false;
  @Input() cityId: string | number;
  @Input() countryName: string = '';
  @Input() cityName: string = '';
  @Input() groupEventsFilters: Record<string, any> = {};
  @Input() occasionGeniusResponse: OccasionGeniusResponse =
    new OccasionGeniusResponse();
  @Input() groupEvents: Array<
    OccasionGeniusEvent | IOccasionGeniusRecurringEvent
  > = [];

  protected readonly swiperOptions: SwiperOptions =
    OCCASION_LIST_SWIPER_OPTIONS;
  protected readonly eSvgIcons = ESvgIcons;

  private destroy$ = new Subject<void>();

  filters: MatBottomSheetRef<ShowAllMobileComponent> | null = null;

  constructor(
    private headerStateService: HeaderStateService,
    private bottomSheet: MatBottomSheet,
    protected breakpointService: BreakpointService,
    protected authService: AuthService,
    private googleAnalyticService: GoogleAnalyticService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private packageService: PackageService,
  ) {}

  ngOnInit() {
    this.headerStateService.backBtnClickEmmit
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.filters) {
          this.headerStateService.isBackBtnPresent = false;
          this.filters.dismiss();
          this.filters = null;
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public seeAll(e: MouseEvent): void {
    e.preventDefault();

    this.headerStateService.isBackBtnPresent = true;
    this.filters = this.bottomSheet.open(ShowAllMobileComponent, {
      hasBackdrop: false,
      closeOnNavigation: true,
      panelClass: ['custom-bottom-sheet', 'custom-bottom-sheet--filters'],
      data: {
        items: this.occasionGeniusResponse.eventsSearchedAndSortedCloned,
        occasionGeniusResponse: this.occasionGeniusResponse,
        pageType: ETripCityInfoType.OCCASION_GENIUS,
        cityId: this.cityId,
        cityName: this.cityName,
        countryName: this.countryName,
        currentDestinationIndex: 0,
        packages$: this.packageService.packages$,
      },
    });
  }

  getCardEvent(event: any): OccasionGeniusEvent[] {
    return event?.events?.length ? event.events : [event];
  }

  setHeaderAnalytic(event: HeaderDesktopEvent): void {
    if (this.breakpointService.isMobile()) {
      this.googleAnalyticService.setHeaderMobileAnalytic({
        header_click: event,
        userID: this.authService.userId,
      });
    } else {
      this.googleAnalyticService.setHeaderDesktopAnalytic({
        header_click: event,
        userID: this.authService.userId,
      });
    }
  }

  openAuthDialog(): void {
    this.setHeaderAnalytic('login');
    this.dialog
      .open(AuthDialogComponent, {
        width: '1150px',
        maxWidth: '98vw',
        maxHeight: '98dvh',
        panelClass: 'mobile-container',
      })
      .afterClosed()
      .pipe(filter((i) => !!i))
      .subscribe(() => this.cdr.markForCheck());
  }
}
